import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import type { AccountBillingState } from '@/components/mobile/MobileBillingBanner';
import { useOrganization } from '@/contexts/OrganizationContext';
import {
  hasOrganizationAccess,
  hasFeatureAccess,
  isTrialExpired,
  getPlanFeatures,
  type PlanType,
  type Feature,
  type AccountStatus,
  type AccessPosture
} from '@shared/accessControl';

/**
 * The set of "new" outbound-brand features that LimePhone plans intentionally
 * do NOT include. These must resolve to "no access" for current LimePhone orgs
 * and stay hidden while the plan is still loading (so we never flash them in).
 * Existing LimePhone features are NOT listed here and therefore stay visible
 * during loading to avoid flicker.
 */
const NEW_GATED_FEATURE_LIST: Feature[] = [
  'skipTrace',
  // Growth includes powerDialer, but it still belongs here: a feature that is
  // gated for SOME plans must stay hidden until the plan is known, or every
  // Starter customer sees it flash in on every page load.
  'powerDialer',
  'predictiveDialer',
  'voicemailDrop',
  'answeringMachineDetection',
  'callScreeningAssist',
  'dripCampaigns',
  'aiCompliance',
  'multiProvider',
];
const NEW_GATED_FEATURES: ReadonlySet<Feature> = new Set(NEW_GATED_FEATURE_LIST);

/**
 * Hook for checking feature access using the simplified access control system
 */
export function useFeatureAccess() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { currentOrganization, isLoading: isOrgLoading } = useOrganization();

  const isLoading = isAuthLoading || isOrgLoading;

  // Only asked once the trial date has passed: the server decides whether the
  // billing timeline's grace window still applies (it depends on server env).
  const trialExpiresAt = (currentOrganization as any)?.trialExpiresAt ?? null;
  const trialEndPassed = trialExpiresAt ? isTrialExpired(trialExpiresAt) : false;
  const { data: accountState } = useQuery<AccountBillingState>({
    queryKey: ['/api/billing/account-state'],
    enabled: trialEndPassed,
    staleTime: 60_000,
  });

  if (isLoading || !currentOrganization) {
    // Loading-state policy: deny the NEW outbound-only features (so they never
    // flash in), but allow EXISTING features through so the current LimePhone
    // nav doesn't flicker/disappear while the plan is still resolving.
    const canWhileLoading = (feature: Feature): boolean => !NEW_GATED_FEATURES.has(feature);
    return {
      orgStatus: 'pending_verification',
      memberStatus: 'invited',
      effectiveStatus: 'pending_verification',
      accessLevel: 'locked',
      isLoading: true,
      isLockedOut: true,
      isBillingOnly: false,
      showUpgradePrompt: false,
      featureAccess: {
        features: {},
        restrictions: {}
      },
      checkFeatureAccess: () => false,
      getRestriction: () => false,
      // Plan-membership feature gate (see `can`/`features` docs below).
      features: [] as Feature[],
      can: canWhileLoading,
    };
  }

  // Map organization status to our simplified types
  const lifecycle = currentOrganization.status === 'active' ? 'active' as AccountStatus :
    currentOrganization.status === 'trial_active' ? 'trialing' as AccountStatus :
    currentOrganization.status === 'suspended' ? 'canceled' as AccountStatus :
    'trialing' as AccountStatus;

  const accessPosture = (currentOrganization as any).accessPosture || 'normal' as AccessPosture;
  
  // Get plan type from subscription or default to trial.
  // Prefer the detailed subscription's `currentPlan.type`; fall back to the
  // canonical `planType` field on the Organization (see OrganizationContext).
  const planType = ((currentOrganization as any).currentPlan?.type
    || currentOrganization.planType
    || 'trial') as PlanType;
  
  // Check if trial is expired. An ended trial inside the billing timeline's
  // grace window (server says trialGrace) keeps full access until suspension.
  const trialExpired = trialEndPassed && !accountState?.trialGrace;

  // Determine access level based on status
  const getAccessLevel = () => {
    if (accessPosture === 'suspended' || accessPosture === 'locked') return 'locked';
    if (accessPosture === 'restricted') return 'billing_only';
    if (lifecycle === 'canceled') return 'locked';
    if (trialExpired && lifecycle === 'trialing') return 'billing_only';
    return 'full';
  };

  const accessLevel = getAccessLevel();
  const orgStatus = currentOrganization.status;
  const memberStatus = currentOrganization?.membership?.status || 'active';
  const effectiveStatus = `${orgStatus}_${memberStatus}`;

  // Helper function to check specific feature access
  const checkFeatureAccess = (feature: Feature): boolean => {
    if (isLoading) return false;
    return hasOrganizationAccess(lifecycle, accessPosture, planType, feature, trialExpired);
  };

  // Pure plan-membership feature list for the current plan. New outbound-only
  // features (skipTrace, dialers, etc.) are absent from every LimePhone plan,
  // so `can()` returns false for them while leaving existing features intact.
  const features: Feature[] = getPlanFeatures(planType);
  const featureSet = new Set<Feature>(features);
  const can = (feature: Feature): boolean => featureSet.has(feature);

  // Simplified restriction getter
  const getRestriction = (restriction: string): boolean => {
    return accessLevel !== 'full';
  };

  // Status checks
  const isLockedOut = accessLevel === 'locked';
  const isBillingOnly = accessLevel === 'billing_only';
  const isReadOnly = false; // Simplified: no read-only access level in this system
  const hasFullAccess = accessLevel === 'full';
  const showUpgradePrompt = accessLevel === 'billing_only' || trialExpired;
  
  // Feature access object
  const featureAccess = {
    features: {
      sms: checkFeatureAccess('sms'),
      voice: checkFeatureAccess('voice'),
      aiVoice: checkFeatureAccess('aiVoice'),
      phoneNumbers: checkFeatureAccess('phoneNumbers'),
      campaigns: checkFeatureAccess('campaigns'),
      analytics: checkFeatureAccess('analytics'),
      integrations: checkFeatureAccess('integrations'),
      api: checkFeatureAccess('api'),
      teamManagement: checkFeatureAccess('teamManagement')
    },
    restrictions: {}
  };

  return {
    // Status information
    orgStatus,
    memberStatus,
    effectiveStatus,
    accessLevel,
    
    // Access flags
    isLoading,
    isLockedOut,
    isBillingOnly,
    isReadOnly,
    hasFullAccess,
    showUpgradePrompt,
    
    // Feature access
    featureAccess,
    checkFeatureAccess,
    getRestriction,

    // Plan-membership feature gate.
    // `features` is the list returned by getPlanFeatures(planType); `can(feature)`
    // is true iff the current plan includes that feature. Use these (or the
    // <FeatureGate> component) to hide nav/UI for features the plan lacks.
    features,
    can,
    
    // Individual feature checks for convenience
    canUseSMS: checkFeatureAccess('sms'),
    canUseVoice: checkFeatureAccess('voice'),
    canUseAIVoice: checkFeatureAccess('aiVoice'),
    canManagePhoneNumbers: checkFeatureAccess('phoneNumbers'),
    canUseCampaigns: checkFeatureAccess('campaigns'),
    canUseIntegrations: checkFeatureAccess('integrations'),
    canUseAPI: checkFeatureAccess('api'),
    canAccessAnalytics: checkFeatureAccess('analytics'),
    canManageTeam: checkFeatureAccess('teamManagement'),
  };
}